/* eslint-disable no-nested-ternary */
/* eslint-disable react/react-in-jsx-scope */
import { useCallback, useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { techInsightsApiRef } from '@backstage-community/plugin-tech-insights';
import { InfoCard, Table as BackstageTable, TableColumn, Link } from '@backstage/core-components';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import WarningIcon from '@material-ui/icons/Warning';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { FixPreviewDialog } from '../FixPreviewDialog/FixPreviewDialog';
import type { FixableCheck } from '../FixPreviewDialog/FixPreviewDialog';
import { aiReadinessFixApiRef, type AiReadinessFixApi, type ScanStatus } from '../../api/aiReadinessFixApi';

/** Cooldown matches the backend POLL_DEADLINE_MS so the button stays disabled
 *  while the workflow run URL is being resolved server-side. */
const DISPATCH_COOLDOWN_MS = 15_000;

const useStyles = makeStyles(theme => ({
  passIcon: { color: theme.palette.success?.main ?? '#4caf50' },
  errorIcon: { color: theme.palette.error.main },
  warnIcon: { color: theme.palette.warning?.main ?? '#ff9800' },
  evidence: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    fontStyle: 'italic',
    marginTop: theme.spacing(0.5),
  },
  solution: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.25),
  },
  emptyState: { padding: theme.spacing(3), textAlign: 'center' },
  checkRow: { '&:last-child td': { borderBottom: 0 } },
  fixTypeChip: {
    marginLeft: theme.spacing(1),
    height: 18,
    fontSize: '0.65rem',
  },
  categoryScore: {
    marginLeft: 'auto',
    marginRight: theme.spacing(1),
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  accordionSummaryContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  issuesTable: {
    '& thead th:first-child, & tbody td:first-child': {
      width: '48px !important',
      padding: '0 !important',
      textAlign: 'center',
    },
    '& tbody td:first-child > span': {
      marginLeft: '0 !important',
    },
  },
}));

interface CheckResult {
  result: boolean;
  check: {
    id: string;
    name: string;
    description: string;
    metadata?: {
      category?: string;
      severity?: string;
      solution?: string;
      rank?: number;
      remediationAction?: string;
      evidenceFactId?: string;
    } & Record<string, unknown>;
  };
}

interface IssueRow {
  id: string;
  name: string;
  category: string;
  severity: string;
  evidence: string;
  solution: string;
  fixType: string;
  remediationAction: string;
  severityRank: number;
  _original: CheckResult;
}

function remediationToFixType(action?: string): string {
  if (action === 'add-annotation' || action === 'add-file') return 'programmatic';
  if (action === 'agent-fix') return 'agent';
  return '';
}

function toIssueRows(checks: CheckResult[], evidenceMap: Map<string, string>): IssueRow[] {
  return checks.map(c => {
    const remediationAction = (c.check.metadata?.remediationAction as string | undefined) ?? '';
    const evidenceFactId = c.check.metadata?.evidenceFactId;
    const evidence = evidenceFactId ? (evidenceMap.get(evidenceFactId) ?? '') : '';
    return {
      id: c.check.id,
      name: c.check.name,
      category: (c.check.metadata?.category as string) ?? 'General',
      severity: (c.check.metadata?.severity as string) ?? 'warning',
      evidence,
      solution: (c.check.metadata?.solution as string) ?? '',
      fixType: remediationToFixType(remediationAction),
      remediationAction,
      severityRank: c.check.metadata?.severity === 'error' ? 0 : 1,
      _original: c,
    };
  });
}

function groupByCategory(results: CheckResult[]): Record<string, CheckResult[]> {
  return results.reduce<Record<string, CheckResult[]>>((acc, result) => {
    const category = (result.check.metadata?.category as string | undefined) ?? 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(result);
    return acc;
  }, {});
}

function CheckIcon({ result, severity }: { result: boolean; severity?: string }) {
  const classes = useStyles();
  if (result) return <CheckCircleIcon className={classes.passIcon} fontSize="small" />;
  if (severity === 'error') return <ErrorIcon className={classes.errorIcon} fontSize="small" />;
  return <WarningIcon className={classes.warnIcon} fontSize="small" />;
}

function FixTypeChip({ fixType }: { fixType?: string }) {
  const classes = useStyles();
  if (!fixType) return null;
  return (
    <Chip
      label={fixType === 'programmatic' ? 'auto-fix' : 'agent fix'}
      size="small"
      color={fixType === 'programmatic' ? 'primary' : 'default'}
      className={classes.fixTypeChip}
    />
  );
}

function FixCard({
  selected,
  entityRef,
}: {
  selected: CheckResult[];
  entityRef: string;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const fixableSelected = selected.filter(i =>
    remediationToFixType(i.check.metadata?.remediationAction as string | undefined),
  );

  const fixableChecks: FixableCheck[] = fixableSelected.map(i => ({
    checkId: i.check.id,
    checkName: i.check.name,
    remediationAction: i.check.metadata?.remediationAction as string | undefined,
    category: i.check.metadata?.category as string | undefined,
  }));

  return (
    <InfoCard title="Fix">
      {selected.length === 0 ? (
        <Typography variant="body2" color="textSecondary" paragraph>
          Select issues from the table to fix.
        </Typography>
      ) : (
        <Typography variant="body2" paragraph>
          {fixableSelected.length} fixable issue{fixableSelected.length !== 1 ? 's' : ''} selected
          {selected.length !== fixableSelected.length && (
            <> ({selected.length - fixableSelected.length} not auto-fixable)</>
          )}
        </Typography>
      )}
      <Tooltip
        title={
          fixableSelected.length === 0
            ? 'Select fixable issues to enable'
            : `Fix ${fixableSelected.length} issue${fixableSelected.length > 1 ? 's' : ''}`
        }
      >
        <span>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={fixableSelected.length === 0}
            onClick={() => setDialogOpen(true)}
          >
            Fix Selected ({fixableSelected.length})
          </Button>
        </span>
      </Tooltip>
      <FixPreviewDialog
        open={dialogOpen}
        entityRef={entityRef}
        checks={fixableChecks}
        onClose={() => setDialogOpen(false)}
      />
    </InfoCard>
  );
}

function CategoryAccordion({
  category,
  results,
  evidenceMap,
  defaultExpanded,
}: {
  category: string;
  results: CheckResult[];
  evidenceMap: Map<string, string>;
  defaultExpanded?: boolean;
}) {
  const classes = useStyles();
  const sorted = [...results].sort((a, b) => {
    const ra = (a.check.metadata?.rank as number | undefined) ?? 99;
    const rb = (b.check.metadata?.rank as number | undefined) ?? 99;
    return ra - rb;
  });
  const passCount = results.filter(r => r.result).length;
  const allPass = passCount === results.length;

  return (
    <Accordion defaultExpanded={defaultExpanded} variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className={classes.accordionSummaryContent}>
          <Typography variant="subtitle1">{category}</Typography>
          <Typography className={classes.categoryScore}>
            {passCount}/{results.length}
          </Typography>
          {!allPass && (
            <Chip
              label={`${results.length - passCount} issue${results.length - passCount > 1 ? 's' : ''}`}
              size="small"
              color="secondary"
              style={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails style={{ padding: 0 }}>
        <Table size="small" style={{ width: '100%' }}>
          <TableBody>
            {sorted.map(checkResult => {
              const severity = checkResult.check.metadata?.severity as string | undefined;
              const solution = checkResult.check.metadata?.solution as string | undefined;
              const evidenceFactId = checkResult.check.metadata?.evidenceFactId;
              const evidence = evidenceFactId ? evidenceMap.get(evidenceFactId) : undefined;
              return (
                <TableRow key={checkResult.check.id} className={classes.checkRow}>
                  <TableCell style={{ width: 32, paddingRight: 0 }}>
                    <Tooltip title={checkResult.result ? 'Passing' : 'Failing'}>
                      <span>
                        <CheckIcon result={checkResult.result} severity={severity} />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{checkResult.check.name}</Typography>
                    {!checkResult.result && evidence && (
                      <Typography className={classes.evidence}>{evidence}</Typography>
                    )}
                    {!checkResult.result && solution && (
                      <Typography className={classes.solution}>{solution}</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
}

function ReadinessCard({
  grouped,
  sortedCategories,
  evidenceMap,
  repoSlug,
  api,
}: {
  grouped: Record<string, CheckResult[]>;
  sortedCategories: string[];
  evidenceMap: Map<string, string>;
  repoSlug: string | undefined;
  api: AiReadinessFixApi;
}) {
  const [dispatching, setDispatching] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [scanStatus, setScanStatus] = useState<ScanStatus | null>(null);
  const [dispatchError, setDispatchError] = useState<string | null>(null);

  const [owner, repo] = (repoSlug ?? '').split('/');

  // Load scan status on mount
  useEffect(() => {
    if (!owner || !repo) return;
    api.getScanStatus(owner, repo).then(setScanStatus).catch(() => {});
  }, [api, owner, repo]);

  // Tick to clear cooldown
  const [, setTick] = useState(0);
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return undefined;
    const id = window.setInterval(() => {
      if (Date.now() >= cooldownUntil) {
        setTick(t => t + 1);
        clearInterval(id);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const isCoolingDown = cooldownUntil > Date.now();
  const canDispatch = !!owner && !!repo && !dispatching && !isCoolingDown;

  const handleDispatch = useCallback(async () => {
    if (!owner || !repo) return;
    setDispatching(true);
    setDispatchError(null);
    try {
      const repoUrl = `https://github.com/${owner}/${repo}`;
      const result = await api.dispatchScan(owner, repo, repoUrl);
      setScanStatus({
        lastDispatchedAt: new Date().toISOString(),
        lastReceivedAt: scanStatus?.lastReceivedAt ?? null,
        workflowRunUrl: result.workflowRunUrl,
      });
      setCooldownUntil(Date.now() + DISPATCH_COOLDOWN_MS);
    } catch (err) {
      setDispatchError(err instanceof Error ? err.message : String(err));
    } finally {
      setDispatching(false);
    }
  }, [api, owner, repo, scanStatus]);

  return (
    <InfoCard title="Readiness">
      {sortedCategories.map((category, idx) => (
        <CategoryAccordion
          key={category}
          category={category}
          results={grouped[category]}
          evidenceMap={evidenceMap}
          defaultExpanded={idx === 0}
        />
      ))}

      <Box mt={2}>
        <Tooltip
          title={
            !owner || !repo
              ? 'No github.com/project-slug annotation found'
              : isCoolingDown
                ? 'Waiting for workflow to start…'
                : dispatching
                  ? 'Dispatching…'
                  : 'Run readiness checks now'
          }
        >
          <span>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              disabled={!canDispatch}
              onClick={() => void handleDispatch()}
            >
              {dispatching ? (
                <>
                  <CircularProgress size={16} style={{ marginRight: 8 }} />
                  Dispatching…
                </>
              ) : isCoolingDown ? (
                'Dispatched — waiting…'
              ) : (
                'Run Readiness Check'
              )}
            </Button>
          </span>
        </Tooltip>
        {dispatchError && (
          <Typography variant="body2" color="error" style={{ marginTop: 4, fontSize: '0.8rem' }}>
            {dispatchError}
          </Typography>
        )}
        {scanStatus?.lastDispatchedAt && (
          <Typography
            variant="body2"
            style={{ marginTop: 6, fontSize: '0.75rem', fontStyle: 'italic' }}
            color="textSecondary"
          >
            Last dispatched{' '}
            {new Date(scanStatus.lastDispatchedAt).toLocaleString()}
            {scanStatus.workflowRunUrl && (
              <>
                {' — '}
                <Link to={scanStatus.workflowRunUrl} target="_blank" rel="noopener noreferrer">
                  view workflow run
                </Link>
              </>
            )}
          </Typography>
        )}
      </Box>
    </InfoCard>
  );
}

export function EntityAiReadinessContent() {
  const classes = useStyles();
  const theme = useTheme();
  const { entity } = useEntity();
  const techInsightsApi = useApi(techInsightsApiRef);
  const aiReadinessApi = useApi(aiReadinessFixApiRef);
  const [results, setResults] = useState<CheckResult[]>([]);
  const [evidenceMap, setEvidenceMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selected, setSelected] = useState<CheckResult[]>([]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const allChecks = await techInsightsApi.getAllChecks();
        const aiChecks = allChecks.filter(c => c.metadata?.remediationAction !== undefined);

        if (aiChecks.length === 0) {
          if (!cancelled) setResults([]);
          return;
        }

        const checkResults = await techInsightsApi.runChecks(
          {
            kind: entity.kind,
            namespace: entity.metadata.namespace ?? 'default',
            name: entity.metadata.name,
          },
          aiChecks.map(c => c.id),
        ) as CheckResult[];

        // Fetch current fact statuses directly from the plugin to:
        // (a) provide evidence strings for checks
        // (b) correct stale Tech Insights cached results with the live plugin status
        // (Tech Insights caches booleans; the plugin DB may have newer pass/fail data)
        const slug = entity.metadata.annotations?.['github.com/project-slug'];
        let liveStatusMap = new Map<string, boolean>();

        if (slug) {
          const [owner, repo] = slug.split('/');
          try {
            const facts = await aiReadinessApi.fetchFacts(owner, repo);
            if (!cancelled) {
              setEvidenceMap(new Map(facts.map(f => [f.id, f.evidence])));
              liveStatusMap = new Map(facts.map(f => [f.id, f.status === 'pass']));
            }
          } catch {
            // non-critical: show checks without evidence or status override
          }
        }

        // For audit checks (those with evidenceFactId), use the live plugin status
        // rather than the potentially-stale cached Tech Insights boolean
        const finalResults = checkResults.map(r => {
          const evidenceFactId = r.check.metadata?.evidenceFactId as string | undefined;
          if (evidenceFactId && liveStatusMap.has(evidenceFactId)) {
            return { ...r, result: liveStatusMap.get(evidenceFactId)! };
          }
          return r;
        });

        if (!cancelled) setResults(finalResults);
      } catch (err) {
        if (!cancelled) setError(err as Error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [entity, techInsightsApi, aiReadinessApi]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.emptyState}>
        <Typography color="error">Failed to load AI readiness checks: {error.message}</Typography>
      </Box>
    );
  }

  if (results.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography color="textSecondary">No AI readiness checks are configured yet.</Typography>
      </Box>
    );
  }

  const failingResults = results.filter(r => !r.result);
  const grouped = groupByCategory(results);
  const categoryOrder = [
    'AI',
    'Catalog',
    'CI/CD',
    'Documentation',
    'Deployment',
    'Jira',
    'PagerDuty',
    'Security',
  ];
  const sortedCategories = Object.keys(grouped).sort((a, b) => {
    const ai = categoryOrder.indexOf(a);
    const bi = categoryOrder.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const entityRef = stringifyEntityRef(entity);
  const issueRows = toIssueRows(failingResults, evidenceMap);

  const columns: TableColumn<IssueRow>[] = [
    {
      title: '',
      field: 'severityRank',
      width: '32px',
      defaultSort: 'asc',
      sorting: true,
      render: (row: IssueRow) => <CheckIcon result={false} severity={row.severity} />,
    },
    {
      title: 'Check',
      field: 'name',
      render: (row: IssueRow) => (
        <Box>
          <Box display="flex" alignItems="center">
            <Typography variant="body2">{row.name}</Typography>
            <FixTypeChip fixType={row.fixType} />
          </Box>
          {row.evidence && (
            <Typography className={classes.evidence}>{row.evidence}</Typography>
          )}
          {row.solution && (
            <Typography className={classes.solution}>{row.solution}</Typography>
          )}
        </Box>
      ),
    },
    {
      title: 'Category',
      field: 'category',
      width: '200px',
    },
  ];

  return (
    <Box p={2}>
      <Box display="flex" flexWrap="wrap" alignItems="flex-start" style={{ gap: 24 }}>
        <Box flex="1 1 480px" minWidth={0} maxWidth="100%" className={classes.issuesTable}>
          <BackstageTable<IssueRow>
            title="Issues"
            options={{
              selection: true,
              paging: true,
              pageSize: 10,
              pageSizeOptions: [10, 20, 50],
              padding: 'dense',
              sorting: true,
              rowStyle: (rowData: IssueRow & { tableData?: { checked?: boolean } }) =>
                rowData.tableData?.checked
                  ? {
                      backgroundColor: theme.palette.action.selected,
                      boxShadow: `inset 3px 0 0 0 ${theme.palette.primary.light}`,
                    }
                  : {},
            }}
            columns={columns}
            data={issueRows}
            onSelectionChange={rows => setSelected((rows as IssueRow[]).map(r => r._original))}
            emptyContent={
              <Box p={3} textAlign="center">
                <Typography color="textSecondary">All checks are passing.</Typography>
              </Box>
            }
          />
        </Box>
        <Box flex="0 0 auto" width={360} maxWidth="100%">
          <FixCard selected={selected} entityRef={entityRef} />
          <Box mt={2}>
            <ReadinessCard
              grouped={grouped}
              sortedCategories={sortedCategories}
              evidenceMap={evidenceMap}
              repoSlug={entity.metadata.annotations?.['github.com/project-slug']}
              api={aiReadinessApi}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
